/* Progressive enhancement only. Shopify renders content and owns commerce. */
(() => {
  'use strict';
  document.documentElement.classList.add('js-enabled');
  const config = window.MUSA || {};
  const query = (s, root = document) => root.querySelector(s);
  const all = (s, root = document) => [...root.querySelectorAll(s)];
  const say = message => { const el = query('#GlobalStatus'); if (el) el.textContent = message; };
  const money = amount => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(amount / 100);
  const apiUrl = path => path.replace(/\/$/, '') + '.js';
  let opener;
  function openDialog(dialog, source) {
    if (!dialog || dialog.open) return;
    all('dialog[open]').forEach(d => d.close());
    opener = source || document.activeElement;
    dialog.showModal();
    all(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'true'));
  }
  function closeDialog(dialog) { if (dialog?.open) dialog.close(); }
  function initDialogs(root = document) {
    all('dialog', root).forEach(dialog => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('click', e => {
        if (e.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) closeDialog(dialog);
        }
      });
      dialog.addEventListener('close', () => {
        all(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
    });
  }
  async function request(url, options = {}) {
    const res = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await res.json();
    if (!res.ok || data.status >= 400) throw new Error(data.description || data.message || config.strings?.error || 'Please try again.');
    return data;
  }
  function updateCount(count) { all('[data-cart-count]').forEach(el => { el.textContent = `(${count})`; }); }
  async function refreshCart(sections) {
    const drawer = query('#CartDrawer');
    if (!drawer) return;
    const sectionId = drawer.dataset.cartSection;
    let html = sections?.[sectionId];
    if (!html) {
      const data = await request(`${config.cart}?sections=${encodeURIComponent(sectionId)}`);
      html = data[sectionId];
    }
    if (!html) throw new Error('Your bag was updated. Open the bag page to review it.');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = query('[data-cart-content]', doc);
    if (!content) throw new Error('Open the bag page to review your items.');
    query('[data-cart-content]', drawer).replaceChildren(...content.childNodes);
    const count = query('[data-cart-count]', doc)?.textContent.match(/\d+/)?.[0];
    if (count !== undefined) updateCount(Number(count));
    if (query('[data-main-cart]')) {
      const pageSection = query('[data-main-cart]').dataset.cartPageSection;
      const fresh = await request(`${config.cart}?sections=${encodeURIComponent(pageSection)}`);
      const cartDoc = new DOMParser().parseFromString(fresh[pageSection] || '', 'text/html');
      const pageContent = query('[data-cart-page-content]', cartDoc);
      if (pageContent) query('[data-cart-page-content]').replaceChildren(...pageContent.childNodes);
    }
  }
  let cartBusy = false;
  async function changeCart(key, quantity, trigger) {
    if (cartBusy || !Number.isInteger(quantity) || quantity < 0) return;
    cartBusy = true;
    const drawer = query('#CartDrawer');
    const sectionId = drawer.dataset.cartSection;
    drawer.classList.add('is-busy');
    try {
      const data = await request(apiUrl(config.cartChange), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: [sectionId], sections_url: location.pathname }) });
      await refreshCart(data.sections);
      say('Bag updated.');
      if (trigger?.dataset.cartChange) query(`[data-cart-change="${CSS.escape(key)}"]`, drawer)?.focus();
    } catch (error) { query('[data-cart-error]', drawer).textContent = error.message; }
    finally { drawer.classList.remove('is-busy'); cartBusy = false; }
  }
  async function addProduct(form) {
    if (form.dataset.busy) return;
    const button = query('[data-add-button]', form);
    if (button?.disabled) return;
    const errorEl = query('[data-product-error]', form);
    const originalLabel = query('[data-add-label]', form)?.textContent;
    form.dataset.busy = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.textContent = '';
    let added = false;
    try {
      const body = new FormData(form);
      const drawer = query('#CartDrawer');
      body.set('sections', drawer.dataset.cartSection);
      body.set('sections_url', location.pathname);
      const data = await request(apiUrl(config.cartAdd), { method: 'POST', body });
      added = true;
      await refreshCart(data.sections);
      openDialog(drawer, button);
      say('Added to your bag.');
    } catch (error) {
      if (errorEl) errorEl.textContent = added ? 'Added to your bag. Open the bag page to review it.' : error.message;
      if (added) { const cart = await request(apiUrl(config.cart)).catch(() => null); if (cart) updateCount(cart.item_count); }
    } finally {
      delete form.dataset.busy;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (originalLabel) query('[data-add-label]', form).textContent = originalLabel;
    }
  }
  const variantRequests = new WeakMap();
  async function selectVariant(section) {
    const ids = all('[data-option-position] input:checked', section).map(el => el.dataset.optionId);
    if (!ids.length) return;
    const previous = variantRequests.get(section);
    previous?.abort();
    const controller = new AbortController();
    variantRequests.set(section, controller);
    const url = new URL(section.dataset.productUrl, location.origin);
    url.searchParams.set('option_values', ids.join(','));
    const historyUrl = new URL(url);
    url.searchParams.set('section_id', section.dataset.productSection);
    const purchase = query('[data-product-purchase]', section);
    const focused = document.activeElement;
    const focusedOption = focused?.dataset.optionId;
    query('[data-add-button]', purchase).disabled = true;
    purchase.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error('Could not check this size. Try again.');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = query('[data-product-purchase]', doc);
      if (!fresh) throw new Error('Could not check this size. Try again.');
      purchase.replaceWith(fresh);
      const current = JSON.parse(query('[data-current-variant]', fresh).textContent || 'null');
      const sticky = query('[data-sticky-add]', section);
      sticky.disabled = !current?.available;
      sticky.textContent = current?.available ? config.strings.add : current ? config.strings.sold : config.strings.unavailable;
      query('[data-sticky-price]', section).textContent = current ? money(current.price) : '';
      if (current?.id) historyUrl.searchParams.set('variant', current.id);
      history.replaceState({}, '', historyUrl.pathname + historyUrl.search);
      if (current?.featured_media?.id) showMedia(section, String(current.featured_media.id));
      if (focusedOption) query(`input[data-option-id="${CSS.escape(focusedOption)}"]`, fresh)?.focus({ preventScroll: true });
      say(current?.available ? `${current.title}. ${money(current.price)}.` : config.strings.sold);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Reload the Shopify product URL to keep inventory and form IDs authoritative.
      location.assign(historyUrl.pathname + historyUrl.search);
    } finally { purchase.removeAttribute('aria-busy'); }
  }
  function showMedia(section, id) {
    all('[data-media-id]', section).forEach(el => {
      el.classList.toggle('is-active', el.dataset.mediaId === id);
      if (el.dataset.mediaId !== id) all('video', el).forEach(video => video.pause());
    });
    all('[data-gallery-link]', section).forEach(el => { el.classList.toggle('is-active', el.dataset.galleryLink === id); el.setAttribute('aria-current', el.dataset.galleryLink === id ? 'true' : 'false'); });
  }
  function initMedia(root = document) {
    all('[data-product-section]', root).forEach(section => {
      const first = query('[data-media-id]', section);
      if (first && !query('[data-media-id].is-active', section)) showMedia(section, first.dataset.mediaId);
      const gallery = query('[data-product-gallery]', section);
      if (gallery && !gallery.dataset.swipeInitialized) {
        gallery.dataset.swipeInitialized = 'true';
        let start;
        gallery.addEventListener('touchstart', e => {
          start = e.touches.length === 1 && !e.target.closest('video,model-viewer,iframe') ? {x:e.touches[0].clientX,y:e.touches[0].clientY} : null;
        }, {passive:true});
        gallery.addEventListener('touchend', e => {
          if (!start || !e.changedTouches.length) return;
          const dx = e.changedTouches[0].clientX - start.x;
          const dy = e.changedTouches[0].clientY - start.y;
          start = null;
          const media = all('[data-media-id]', gallery);
          if (media.length < 2 || Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          e.preventDefault();
          const current = media.findIndex(el => el.classList.contains('is-active'));
          const next = (current + (dx < 0 ? 1 : -1) + media.length) % media.length;
          showMedia(section, media[next].dataset.mediaId);
          say(`${config.strings?.photo || 'Photo'} ${next + 1} / ${media.length}`);
        }, {passive:false});
      }

    });
    all('[data-recommendations-url]', root).forEach(el => {
      if (el.dataset.loaded || el.children.length) return;
      el.dataset.loaded = 'true';
      const observer = new IntersectionObserver(async entries => {
        if (!entries.some(e => e.isIntersecting)) return;
        observer.disconnect();
        try { const res = await fetch(el.dataset.recommendationsUrl); if (!res.ok) return; const doc = new DOMParser().parseFromString(await res.text(), 'text/html'); const content = query('[data-recommendations-url]', doc); if (content) el.replaceChildren(...content.childNodes); } catch { /* A missing recommendation never blocks shopping. */ }
      }, { rootMargin: '400px' });
      observer.observe(el);
    });
  }
  document.addEventListener('click', e => {
    const open = e.target.closest('[data-dialog-open]');
    if (open) { e.preventDefault(); openDialog(document.getElementById(open.dataset.dialogOpen), open); }
    const close = e.target.closest('[data-dialog-close]');
    if (close) closeDialog(close.closest('dialog'));
    const cart = e.target.closest('[data-cart-open]');
    if (cart) { e.preventDefault(); openDialog(query('#CartDrawer'), cart); }
    const change = e.target.closest('[data-cart-change]');
    if (change) { e.preventDefault(); changeCart(change.dataset.cartChange, Number(change.dataset.quantity), change); }
    const gallery = e.target.closest('[data-gallery-link]');
    if (gallery) { e.preventDefault(); showMedia(gallery.closest('[data-product-section]'), gallery.dataset.galleryLink); }
    const sticky = e.target.closest('[data-sticky-add]');
    if (sticky && !sticky.disabled) query('.product-form', sticky.closest('[data-product-section]')).requestSubmit();
  });
  document.addEventListener('submit', e => {
    if (e.target.matches('.product-form')) { e.preventDefault(); addProduct(e.target); }
  });
  document.addEventListener('change', e => {
    if (e.target.matches('[data-variant-options] input')) selectVariant(e.target.closest('[data-product-section]'));
    if (e.target.matches('[data-cart-quantity]')) changeCart(e.target.dataset.cartQuantity, Number(e.target.value), e.target);
  });
  initDialogs(); initMedia();
  document.addEventListener('shopify:section:load', e => { initDialogs(e.target); initMedia(e.target); });
  document.addEventListener('shopify:section:unload', e => { all('dialog[open]', e.target).forEach(d => d.close()); });
})();
